import * as React from 'react';
import { List, Datagrid, FunctionField, Confirm, useNotify, useRefresh, useListContext } from 'react-admin';
import AddIcon from '@mui/icons-material/Add';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, useMediaQuery, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IconButton } from '../icon-button/IconButton';
import { useParams } from 'react-router-dom';
import { EmptyBox } from '../empty-box/EmptyBox';
import { CategoryAddDialog } from '../dialogs/CategoryAddDialog';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CategoryService from '../../services/category/categoryService';
import { PageHeader } from '../page-header/PageHeader';
import { cardActionButtonSx } from '../../core/theme/buttonStyles';
import * as Sentry from '@sentry/react';
import { BackButton } from '../back-button/BackButton';
import { listSurfaceSx } from '../../core/theme/listStyles';

interface LocationCategoryTableActionsProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const LocationCategoryTableActions = ({ open, setOpen }: LocationCategoryTableActionsProps) => {
  const location = useLocation();
  const locationName = (location.state as { locationName?: string } | null)?.locationName;
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:600px)');
  const navigate = useNavigate();
  const { id } = useParams();

  return (
    <Box>
      <PageHeader
        back={<BackButton />}
        title={t('resources.categories')}
        subtitle={locationName}
        stretchActionsOnMobile
        actions={
          <>
            <IconButton
              icon={<SwapVertIcon />}
              label={t('menuOrder.action')}
              sx={isMobile ? cardActionButtonSx : undefined}
              onClick={() =>
                navigate(`/locationManagement/${id}/menu-order`, {
                  state: { locationName: locationName },
                })
              }
            />
            <IconButton
              /* A bare "+" gave no clue what it adds — keep the label on phones
                 too, the header has room for it. */
              icon={<AddIcon />}
              label={t('locationItems.category')}
              sx={isMobile ? cardActionButtonSx : undefined}
              onClick={() => setOpen(true)}
            />
          </>
        }
      />
      <CategoryAddDialog open={open} onClose={() => setOpen(false)} />
    </Box>
  );
};

interface MobileCategoryListProps {
  setConfirmOpen: (open: boolean) => void;
  setToDelete: (id: number | null) => void;
}

const MobileCategoryList = ({ setConfirmOpen, setToDelete }: MobileCategoryListProps) => {
  const { data, isLoading } = useListContext();
  const { t } = useTranslation();

  if (isLoading || !data) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {data.map((record: any) => (
        <Box
          key={record.id}
          sx={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            p: 'var(--space-4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontWeight: 600,
                fontSize: 'var(--text-sm)',
                /* The API does not always return a translated name; a row
                   showing only "#5" is unreadable, so say so explicitly. */
                color: record.name ? undefined : 'var(--color-text-tertiary)',
                fontStyle: record.name ? undefined : 'italic',
              }}
            >
              {record.name || t('category.columns.name')}
            </Typography>
            <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
              #{record.id ?? ''}
            </Typography>
          </Box>
          <IconButton
            icon={<DeleteOutlineIcon />}
            ariaLabel={t('common.delete')}
            color="var(--color-error)"
            onClick={() => {
              setConfirmOpen(true);
              setToDelete(record.id);
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

export const LocationCategoryTable = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDelete, setToDelete] = React.useState<number | null>(null);
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = React.useState(false);
  const categoryService = new CategoryService();
  const isMobile = useMediaQuery('(max-width:600px)');

  return (
    <Box>
      <LocationCategoryTableActions open={open} setOpen={setOpen} />
      <List
        title={t('resources.categories')}
        actions={false}
        filter={{ locationId: id }}
        empty={
          <EmptyBox
            title={t('category.emptyBox.title')}
            description={t('category.emptyBox.description')}
            onClick={() => setOpen(true)}
          />
        }
        perPage={25}
        resource="categories"
        sx={listSurfaceSx({ isMobile })}
      >
        {isMobile ? (
          <MobileCategoryList setConfirmOpen={setConfirmOpen} setToDelete={setToDelete} />
        ) : (
          <Datagrid
            bulkActionButtons={false}
            rowClick={false}
            sx={{ '& .RaDatagrid-headerCell': { fontWeight: 700, letterSpacing: 0.2 } }}
          >
            <FunctionField
              sortable={false}
              label={t('category.columns.id')}
              render={(record: any) => record?.id ?? ''}
            />
            <FunctionField
              sortable={false}
              label={t('category.columns.name')}
              render={(record: any) => record?.name ?? ''}
            />
            <FunctionField
              label=""
              sortable={false}
              render={(record) => (
                <IconButton
                  icon={<DeleteOutlineIcon />}
                  color="var(--color-error)"
                  onClick={() => {
                    setConfirmOpen(true);
                    setToDelete(record.id);
                  }}
                />
              )}
            />
          </Datagrid>
        )}
      </List>
      <Confirm
        isOpen={confirmOpen}
        title={t('category.dialog.confirmDeletionTitle')}
        content={t('category.dialog.confirmDeletionBody')}
        confirm={t('common.delete')}
        cancel={t('common.cancel')}
        onConfirm={async () => {
          try {
            if (toDelete == null) return;
            await categoryService.deleteCategory(toDelete);
            notify(t('common.saved'), { type: 'success' });
            refresh();
            setConfirmOpen(false);
            setToDelete(null);
          } catch (error: any) {
            notify(error.response.data.error, { type: 'error' });
            console.error(error.response.data.error);
            Sentry.captureException(error);
            setConfirmOpen(false);
            setToDelete(null);
          }
        }}
        onClose={() => {
          setConfirmOpen(false);
          setToDelete(null);
        }}
      />
    </Box>
  );
};
